/* eslint-disable react/no-unused-prop-types */
import { Button } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';

import { FilterableItem } from 'src/components/FilterableCollapsibleBlock';
import { Link } from 'src/components/Link';

export type TableCellProps<ItemType extends FilterableItem> = {
  column: React.ReactElement<TableColumnProps<ItemType>>;
  columnIndex: number;
  item: ItemType;
};

export type TableColumnProps<ItemType extends FilterableItem> = {
  itemKey?: keyof ItemType;
  name?: string;
  onClick?: (cellData: TableCellProps<ItemType>) => void;
  TableCell?: React.ComponentType<TableCellProps<ItemType>>;
};

export const TableColumn = <ItemType extends FilterableItem>({
  name,
}: TableColumnProps<ItemType>) => <th>{name}</th>;

export type TableColumnChildren<ItemType extends FilterableItem> =
  | Array<React.ReactElement<TableColumnProps<ItemType>>>
  | React.ReactElement<TableColumnProps<ItemType>>;

export const ButtonTableCell = <ItemType extends FilterableItem>(
  props: TableCellProps<ItemType>
) => {
  const {
    column: {
      props: { itemKey, onClick },
    },
    columnIndex,
    item,
  } = props;
  return (
    <td key={`${item.value}-${columnIndex}`}>
      {itemKey ? (
        <Button
          alignText="left"
          className={classNames('sd-table-drawer-cell-button', {
            'sd-no-events': !onClick,
          })}
          fill
          minimal
          onClick={onClick ? () => onClick(props) : noop}
          tabIndex={onClick ? 0 : -1}
          text={item[itemKey]}
        />
      ) : (
        ''
      )}
    </td>
  );
};

export const LinkTableCell = <ItemType extends FilterableItem>(
  props: TableCellProps<ItemType>
) => {
  const {
    column: {
      props: { itemKey, onClick },
    },
    columnIndex,
    item,
  } = props;
  let cellContent;
  if (!itemKey) {
    cellContent = '';
  } else if (!onClick) {
    cellContent = <Text ellipsize>{item[itemKey]}</Text>;
  } else {
    cellContent = (
      <Link onClick={() => onClick(props)}>
        <Text ellipsize>{item[itemKey]}</Text>
      </Link>
    );
  }
  return <td key={`${item.value}-${columnIndex}`}>{cellContent}</td>;
};

export const TableItemRenderer = <ItemType extends FilterableItem>({
  columns,
  hasHiddenUntilHover,
  item,
}: {
  columns: TableColumnChildren<ItemType>;
  hasHiddenUntilHover?: boolean;
  item: ItemType;
}) => {
  return (
    <tr
      className={classNames({
        'sd-has-hidden-until-hover': hasHiddenUntilHover,
      })}
    >
      {React.Children.map(
        React.Children.toArray(columns) as Array<
          React.ReactElement<TableColumnProps<ItemType>>
        >,
        (column, columnIndex) => {
          const { itemKey, name, TableCell = ButtonTableCell } = column.props;
          return (
            <TableCell
              key={`${name}${String(itemKey)}`}
              column={column}
              columnIndex={columnIndex}
              item={item}
            />
          );
        }
      )}
    </tr>
  );
};
